import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { alpha } from "@mui/material/styles";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CropFreeOutlinedIcon from "@mui/icons-material/CropFreeOutlined";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import CropSquareOutlinedIcon from "@mui/icons-material/CropSquareOutlined";
import FlipToFrontOutlinedIcon from "@mui/icons-material/FlipToFrontOutlined";
import FlipToBackOutlinedIcon from "@mui/icons-material/FlipToBackOutlined";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import ZoomOutOutlinedIcon from "@mui/icons-material/ZoomOutOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ViewQuiltOutlinedIcon from "@mui/icons-material/ViewQuiltOutlined";
import {
  addSkillAgentMoodboardAssets,
  autoSkillAgentMoodboardPages,
  deleteSkillAgentMoodboard,
  deleteSkillAgentMoodboardAsset,
  exportSkillAgentMoodboardPdf,
  fetchSkillAgentMoodboardAssetBlobUrl,
  getSkillAgentMoodboardById,
  getSkillAgentMoodboardPool,
  removeBgSkillAgentMoodboardAsset,
  saveSkillAgentMoodboardPages,
  updateSkillAgentMoodboard,
} from "../../api/Skills/skillAgentMoodboardsApi";
import {
  fetchSkillAgentSearchItemBlobUrl,
  getSkillAgentSearchById,
  listSkillAgentSearches,
} from "../../api/Skills/skillAgentSearchesApi";
import {
  pageFieldSx,
  pagePrimaryButtonSx,
} from "../../styles/pageModernStyles";
import { moodboardStatusLabel } from "./skillAgentTaskTree";
import {
  applyAviatorPresetToPage,
  clampSlot,
  coverFit,
  emptyFreePage,
  newSlotId,
  normalizeFreePage,
  normalizePages,
  snapSlot,
  sortSlotsByLayer,
  splitCategoryLabel,
  splitStudioLine,
} from "./skillAgentMoodboardLayout";

const HANDLES = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];

function AssetThumb({
  assetId,
  onClick,
  selected,
  zoom = 1,
  panX = 0.5,
  panY = 0.5,
  fillSlot = false,
  transparentBg = false,
}) {
  const boxRef = useRef(null);
  const [src, setSrc] = useState("");
  const [nat, setNat] = useState(null);
  const [boxPx, setBoxPx] = useState({ w: 0, h: 0 });

  useEffect(() => {
    let revoked = false;
    let url = "";
    setNat(null);
    (async () => {
      try {
        url = await fetchSkillAgentMoodboardAssetBlobUrl(assetId);
        if (!revoked) setSrc(url);
      } catch {
        if (!revoked) setSrc("");
      }
    })();
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [assetId]);

  useEffect(() => {
    if (!fillSlot) return undefined;
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const apply = () => {
      const r = el.getBoundingClientRect();
      setBoxPx({ w: r.width, h: r.height });
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fillSlot, src]);

  const fitted =
    fillSlot && nat && boxPx.w > 1 && boxPx.h > 1
      ? coverFit(
          nat.w,
          nat.h,
          { x: 0, y: 0, w: boxPx.w, h: boxPx.h },
          {
            zoom,
            panX,
            panY,
          },
        )
      : null;

  return (
    <Box
      ref={boxRef}
      onClick={onClick}
      sx={{
        width: "100%",
        height: "100%",
        bgcolor: transparentBg
          ? "transparent"
          : fillSlot
            ? "#ebebeb"
            : "action.hover",
        cursor: onClick ? "pointer" : "default",
        outline: selected ? "2px solid" : "none",
        outlineColor: "primary.main",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {src ? (
        <Box
          component="img"
          src={src}
          alt=""
          draggable={false}
          data-slot-img="1"
          onLoad={(e) => {
            const img = e.currentTarget;
            if (img?.naturalWidth && img?.naturalHeight) {
              setNat({ w: img.naturalWidth, h: img.naturalHeight });
            }
          }}
          sx={
            fillSlot && fitted
              ? {
                  position: "absolute",
                  left: fitted.x,
                  top: fitted.y,
                  width: fitted.w,
                  height: fitted.h,
                  objectFit: "fill",
                  display: "block",
                  pointerEvents: "none",
                  userSelect: "none",
                }
              : {
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                  pointerEvents: "none",
                  opacity: fillSlot ? 0 : 1,
                }
          }
        />
      ) : (
        <Box sx={{ display: "grid", placeItems: "center", height: "100%" }}>
          <CircularProgress size={14} />
        </Box>
      )}
    </Box>
  );
}

function clamp01Local(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0.5;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

function PoolThumb({ item, checked, onToggle, onAdd, height = 148 }) {
  const [src, setSrc] = useState("");

  useEffect(() => {
    let revoked = false;
    let url = "";
    (async () => {
      try {
        url = await fetchSkillAgentSearchItemBlobUrl(item.id);
        if (!revoked) setSrc(url);
      } catch {
        if (!revoked) setSrc("");
      }
    })();
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [item.id]);

  return (
    <Box
      onClick={() => onAdd?.(item)}
      title="Clic para añadir al moodboard"
      sx={{
        position: "relative",
        borderRadius: 1.5,
        overflow: "hidden",
        border: "1px solid",
        borderColor: checked ? "primary.main" : "divider",
        cursor: "pointer",
        bgcolor: "#eee",
        height,
        minHeight: height,
        maxHeight: height,
      }}
    >
      <Checkbox
        size="small"
        checked={checked}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onToggle?.(e.target.checked)}
        sx={{
          position: "absolute",
          top: 4,
          left: 4,
          zIndex: 2,
          p: 0.25,
          bgcolor: alpha("#fff", 0.92),
        }}
      />
      {src ? (
        <Box
          component="img"
          src={src}
          alt={item.Title || item.BrandName || ""}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center top",
            display: "block",
          }}
        />
      ) : (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
          }}
        >
          <CircularProgress size={18} />
        </Box>
      )}
      <Typography
        variant="caption"
        noWrap
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
          px: 0.75,
          py: 0.4,
          fontSize: "0.68rem",
          fontWeight: 700,
          color: "#fff",
          bgcolor: "rgba(0,0,0,0.55)",
          lineHeight: 1.25,
        }}
      >
        {item.BrandName || item.Title || `#${item.id}`}
        {item.SearchTitulo ? ` · ${item.SearchTitulo}` : ""}
      </Typography>
    </Box>
  );
}

function FreeformSlotBox({
  slot,
  selected,
  onSelect,
  onChange,
  onRequestRemove,
  canRemove,
  canvasRef,
}) {
  const dragRef = useRef(null);

  const emitSlot = (partial, { snap = true } = {}) => {
    const next = clampSlot({
      ...slot,
      ...partial,
      id: slot.id,
      assetId: slot.assetId,
    });
    onChange?.(snap ? snapSlot(next) : next);
  };

  const startGesture = (mode, handle, e) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect?.(slot.id);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const slotRect = e.currentTarget.getBoundingClientRect();
    const imgEl = e.currentTarget.querySelector("img[data-slot-img]");
    const iw = Math.max(1, imgEl?.naturalWidth || 1);
    const ih = Math.max(1, imgEl?.naturalHeight || 1);
    dragRef.current = {
      mode,
      handle,
      startX: e.clientX,
      startY: e.clientY,
      orig: { ...slot },
      cw: rect.width,
      ch: rect.height,
      sw: Math.max(1, slotRect.width),
      sh: Math.max(1, slotRect.height),
      iw,
      ih,
    };
    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      let next = { ...d.orig };
      if (d.mode === "pan") {
        // Mover en píxeles reales de la casilla (misma caja que el render).
        const dx = ev.clientX - d.startX;
        const dy = ev.clientY - d.startY;
        const box = { x: 0, y: 0, w: d.sw, h: d.sh };
        const fitted0 = coverFit(d.iw, d.ih, box, {
          zoom: d.orig.zoom,
          panX: d.orig.panX,
          panY: d.orig.panY,
        });
        const newX = fitted0.x + dx;
        const newY = fitted0.y + dy;
        const denomW = box.w - fitted0.w;
        const denomH = box.h - fitted0.h;
        next.panX =
          Math.abs(denomW) < 0.0001
            ? 0.5
            : clamp01Local((newX - box.x) / denomW);
        next.panY =
          Math.abs(denomH) < 0.0001
            ? 0.5
            : clamp01Local((newY - box.y) / denomH);
        onChange?.(
          clampSlot({
            ...next,
            id: slot.id,
            assetId: slot.assetId,
          }),
        );
        return;
      }
      if (d.mode === "move") {
        const dx = (ev.clientX - d.startX) / d.cw;
        const dy = (ev.clientY - d.startY) / d.ch;
        next.x = d.orig.x + dx;
        next.y = d.orig.y + dy;
      } else {
        const dx = (ev.clientX - d.startX) / d.cw;
        const dy = (ev.clientY - d.startY) / d.ch;
        const h = d.handle;
        if (h.includes("e")) next.w = d.orig.w + dx;
        if (h.includes("s")) next.h = d.orig.h + dy;
        if (h.includes("w")) {
          next.x = d.orig.x + dx;
          next.w = d.orig.w - dx;
        }
        if (h.includes("n")) {
          next.y = d.orig.y + dy;
          next.h = d.orig.h - dy;
        }
      }
      onChange?.(
        snapSlot(
          clampSlot({
            ...next,
            id: slot.id,
            assetId: slot.assetId,
          }),
        ),
      );
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const onWheel = (e) => {
    if (!selected || !slot.assetId) return;
    e.preventDefault();
    e.stopPropagation();
    const delta = e.deltaY > 0 ? -0.12 : 0.12;
    const zoom = Math.min(4, Math.max(0.25, (Number(slot.zoom) || 1) + delta));
    emitSlot({ zoom }, { snap: false });
  };

  const bumpZoom = (dir) => {
    const zoom = Math.min(
      4,
      Math.max(0.25, (Number(slot.zoom) || 1) + dir * 0.25),
    );
    emitSlot({ zoom }, { snap: false });
  };

  const pinchDist = (t0, t1) => {
    const dx = t0.clientX - t1.clientX;
    const dy = t0.clientY - t1.clientY;
    return Math.hypot(dx, dy);
  };

  const onTouchStart = (e) => {
    if (!slot.assetId || e.touches.length !== 2) return;
    e.preventDefault();
    e.stopPropagation();
    onSelect?.(slot.id);
    dragRef.current = {
      mode: "pinch",
      orig: { ...slot },
      startDist: pinchDist(e.touches[0], e.touches[1]),
    };
  };

  const onTouchMove = (e) => {
    const d = dragRef.current;
    if (!d || d.mode !== "pinch" || e.touches.length !== 2) return;
    e.preventDefault();
    e.stopPropagation();
    const dist = pinchDist(e.touches[0], e.touches[1]);
    if (!d.startDist || d.startDist < 1) return;
    const ratio = dist / d.startDist;
    const zoom = Math.min(
      4,
      Math.max(0.25, (Number(d.orig.zoom) || 1) * ratio),
    );
    onChange?.(
      clampSlot({
        ...d.orig,
        id: slot.id,
        assetId: slot.assetId,
        zoom,
      }),
    );
  };

  const onTouchEnd = () => {
    if (dragRef.current?.mode === "pinch") dragRef.current = null;
  };

  const isCircle = String(slot.shape || "rect") === "circle";
  const hasAsset = Boolean(slot.assetId);

  return (
    <Box
      onPointerDown={(e) => {
        // Ignore second pointer during pinch (touch handles it).
        if (e.pointerType === "touch" && dragRef.current?.mode === "pinch") {
          return;
        }
        // Foto dentro: arrastrar = pan. Shift = mover la casilla.
        if (slot.assetId && !e.shiftKey) {
          startGesture("pan", null, e);
        } else {
          startGesture("move", null, e);
        }
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      onWheel={onWheel}
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.(slot.id);
      }}
      sx={{
        position: "absolute",
        left: `${slot.x * 100}%`,
        top: `${slot.y * 100}%`,
        width: `${slot.w * 100}%`,
        height: `${slot.h * 100}%`,
        bgcolor: hasAsset ? "transparent" : "transparent",
        overflow: "hidden",
        borderRadius: isCircle ? "50%" : 0,
        outline: selected
          ? "2px solid"
          : hasAsset
            ? "1px solid transparent"
            : "1px dashed rgba(0,0,0,0.28)",
        outlineColor: selected ? "primary.main" : undefined,
        outlineOffset: -1,
        cursor: selected && slot.assetId ? "grab" : "move",
        zIndex: selected
          ? 1000 + (Number(slot.z) || 0)
          : 1 + (Number(slot.z) || 0),
        touchAction: "none",
        userSelect: "none",
        "&:active": {
          cursor: selected && slot.assetId ? "grabbing" : "move",
        },
        "&:hover .slot-remove, &:hover .slot-zoom": { opacity: 1 },
      }}
    >
      {slot.assetId ? (
        <AssetThumb
          assetId={slot.assetId}
          fillSlot
          transparentBg
          zoom={slot.zoom}
          panX={slot.panX}
          panY={slot.panY}
        />
      ) : (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "grid",
            placeItems: "center",
            height: "100%",
            fontSize: "0.65rem",
            pointerEvents: "none",
          }}
        >
          {isCircle ? "○ vacía" : "vacía"}
        </Typography>
      )}
      {selected && slot.assetId ? (
        <Stack
          className="slot-zoom"
          direction="row"
          spacing={0.25}
          sx={{
            position: "absolute",
            left: 4,
            bottom: 4,
            zIndex: 7,
            opacity: 1,
            bgcolor: alpha("#0f172a", 0.78),
            borderRadius: 1.5,
            p: 0.35,
          }}
        >
          <Tooltip title="Alejar">
            <IconButton
              size="small"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                bumpZoom(-1);
              }}
              disabled={(slot.zoom || 1) <= 0.25}
              sx={{ color: "#fff", p: 0.75, minWidth: 36, minHeight: 36 }}
            >
              <ZoomOutOutlinedIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </Tooltip>
          <Typography
            variant="caption"
            sx={{
              color: "#fff",
              fontWeight: 700,
              px: 0.5,
              alignSelf: "center",
              fontSize: "0.75rem",
              minWidth: 36,
              textAlign: "center",
            }}
          >
            {Math.round((slot.zoom || 1) * 100)}%
          </Typography>
          <Tooltip title="Acercar">
            <IconButton
              size="small"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                bumpZoom(1);
              }}
              disabled={(slot.zoom || 1) >= 4}
              sx={{ color: "#fff", p: 0.75, minWidth: 36, minHeight: 36 }}
            >
              <ZoomInOutlinedIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      ) : null}
      {canRemove ? (
        <Tooltip title="Quitar casilla">
          <IconButton
            className="slot-remove"
            size="small"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onRequestRemove?.(slot.id);
            }}
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              zIndex: 7,
              opacity: selected ? 1 : 0,
              transition: "opacity 0.12s",
              bgcolor: alpha("#fff", 0.92),
              boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
              p: 0.35,
              "&:hover": { bgcolor: "#fff", color: "error.main" },
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ) : null}
      {selected
        ? HANDLES.map((h) => (
            <Box
              key={h}
              onPointerDown={(e) => startGesture("resize", h, e)}
              sx={{
                position: "absolute",
                width: 10,
                height: 10,
                bgcolor: "primary.main",
                border: "1px solid #fff",
                borderRadius: 0.25,
                zIndex: 6,
                ...(h.includes("n") ? { top: -5 } : null),
                ...(h.includes("s") ? { bottom: -5 } : null),
                ...(h.includes("w") ? { left: -5 } : null),
                ...(h.includes("e") ? { right: -5 } : null),
                ...(h === "n" || h === "s"
                  ? { left: "50%", marginLeft: "-5px" }
                  : null),
                ...(h === "e" || h === "w"
                  ? { top: "50%", marginTop: "-5px" }
                  : null),
                cursor: `${h}-resize`,
              }}
            />
          ))
        : null}
    </Box>
  );
}

async function loadPoolFromSearches(board) {
  const customerId = board?.CustomerId ? Number(board.CustomerId) : null;
  const temporada = String(board?.Temporada || "").trim();
  const list = await listSkillAgentSearches({
    ...(customerId ? { customerId } : {}),
    ...(temporada ? { temporada } : {}),
  });
  const searches = (Array.isArray(list) ? list : []).filter((s) => {
    if (customerId && Number(s.CustomerId) !== customerId) return false;
    if (temporada && String(s.Temporada || "").trim() !== temporada) {
      return false;
    }
    return true;
  });
  const items = [];
  for (const s of searches) {
    const detail = await getSkillAgentSearchById(s.id);
    const imgs = (Array.isArray(detail?.items) ? detail.items : []).filter(
      (it) => !it.Kind || String(it.Kind).toLowerCase() === "image",
    );
    for (const it of imgs) {
      items.push({
        id: it.id,
        SearchId: s.id,
        Title: it.Title,
        BrandName: it.BrandName,
        BrandId: it.BrandId,
        SourceUrl: it.SourceUrl,
        MimeType: it.MimeType,
        DriveFileUrl: it.DriveFileUrl,
        SearchTitulo: s.Titulo || null,
      });
    }
  }
  return {
    searches: searches.map((s) => ({
      id: s.id,
      Titulo: s.Titulo,
      Status: s.Status,
      Temporada: s.Temporada,
      CustomerId: s.CustomerId,
    })),
    items,
  };
}

export default function SkillAgentMoodboardDetail({
  moodboardId,
  onChanged,
  onDeleted,
}) {
  const canvasRef = useRef(null);
  const skipAutosaveRef = useRef(true);
  const pagesRef = useRef([emptyFreePage()]);
  const metaRef = useRef({ titulo: "", categoryLabel: "", studioLine: "" });
  const pagesTimerRef = useRef(null);
  const metaTimerRef = useRef(null);
  const onChangedRef = useRef(onChanged);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [saveState, setSaveState] = useState("saved"); // saved | saving | unsaved | error
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [board, setBoard] = useState(null);
  const [assets, setAssets] = useState([]);
  const [pages, setPages] = useState([emptyFreePage()]);
  const [pageIdx, setPageIdx] = useState(0);
  const [activeSlotId, setActiveSlotId] = useState(null);
  const [pool, setPool] = useState({ searches: [], items: [] });
  const [poolPick, setPoolPick] = useState(() => new Set());
  const [poolError, setPoolError] = useState("");
  const [poolLoading, setPoolLoading] = useState(false);
  const [poolShowCount, setPoolShowCount] = useState(24);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [slotRemoveId, setSlotRemoveId] = useState(null);
  const [titulo, setTitulo] = useState("");
  const [categoryLabel, setCategoryLabel] = useState("");
  const [studioLine, setStudioLine] = useState("");
  const [metaOpen, setMetaOpen] = useState(false);
  const [photosTab, setPhotosTab] = useState(0);
  const [layoutMenuEl, setLayoutMenuEl] = useState(null);

  const load = useCallback(async () => {
    if (!moodboardId) return;
    skipAutosaveRef.current = true;
    setLoading(true);
    setError("");
    setPoolError("");
    setSaveState("saved");
    try {
      const data = await getSkillAgentMoodboardById(moodboardId);
      setBoard(data);
      setAssets(Array.isArray(data?.assets) ? data.assets : []);
      setPages(normalizePages(data?.PagesJson));
      setTitulo(data?.Titulo || "");
      setCategoryLabel(data?.CategoryLabel || "");
      setStudioLine(data?.StudioLine || "");
      setActiveSlotId(null);
      setPoolShowCount(24);

      setPoolLoading(true);
      let poolData = { searches: [], items: [] };
      try {
        poolData = await getSkillAgentMoodboardPool(moodboardId);
      } catch (poolErr) {
        try {
          poolData = await loadPoolFromSearches(data);
        } catch {
          setPoolError(
            poolErr?.response?.data?.error ||
              "No se pudo cargar el pool de SEARCH.",
          );
          poolData = { searches: [], items: [] };
        }
      }
      if (
        !Array.isArray(poolData?.items) ||
        (!poolData.items.length && data?.CustomerId)
      ) {
        try {
          const fallback = await loadPoolFromSearches(data);
          if (fallback.items?.length) poolData = fallback;
        } catch {
          /* keep primary */
        }
      }
      setPool({
        searches: Array.isArray(poolData?.searches) ? poolData.searches : [],
        items: Array.isArray(poolData?.items) ? poolData.items : [],
      });
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo cargar el moodboard.");
    } finally {
      setPoolLoading(false);
      setLoading(false);
      // Enable autosave after React applies the loaded state.
      setTimeout(() => {
        skipAutosaveRef.current = false;
      }, 100);
    }
  }, [moodboardId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    onChangedRef.current = onChanged;
  }, [onChanged]);

  const usedSearchItemIds = useMemo(() => {
    const s = new Set();
    for (const a of assets) {
      if (a.SearchItemId) s.add(Number(a.SearchItemId));
    }
    return s;
  }, [assets]);

  const currentPage = useMemo(
    () => normalizeFreePage(pages[pageIdx] || emptyFreePage()),
    [pages, pageIdx],
  );
  const activeSlot = useMemo(
    () => (currentPage.slots || []).find((s) => s.id === activeSlotId) || null,
    [currentPage, activeSlotId],
  );
  const layeredSlots = useMemo(
    () => sortSlotsByLayer(currentPage.slots || []),
    [currentPage],
  );

  const applyBoard = (data, { fromServer = true } = {}) => {
    if (!data) return;
    if (fromServer) skipAutosaveRef.current = true;
    setBoard(data);
    if (Array.isArray(data.assets)) setAssets(data.assets);
    if (data.PagesJson) setPages(normalizePages(data.PagesJson));
    if (data.Titulo != null) setTitulo(data.Titulo);
    if (data.CategoryLabel != null) setCategoryLabel(data.CategoryLabel);
    if (data.StudioLine != null) setStudioLine(data.StudioLine);
    if (fromServer) {
      setTimeout(() => {
        skipAutosaveRef.current = false;
      }, 80);
    }
  };

  const updateCurrentPageSlots = (updater) => {
    setPages((prev) =>
      prev.map((p, i) => {
        if (i !== pageIdx) return p;
        const base = normalizeFreePage(p);
        const slots =
          typeof updater === "function" ? updater(base.slots) : updater;
        return { slots: slots.map((s) => clampSlot(s)) };
      }),
    );
  };

  useEffect(() => {
    pagesRef.current = pages;
  }, [pages]);

  useEffect(() => {
    metaRef.current = { titulo, categoryLabel, studioLine };
  }, [titulo, categoryLabel, studioLine]);

  const persistPages = useCallback(async () => {
    if (!moodboardId || exporting || skipAutosaveRef.current) return false;
    const payload = pagesRef.current.map((p) => normalizeFreePage(p));
    setSaveState("saving");
    try {
      const data = await saveSkillAgentMoodboardPages(moodboardId, payload);
      applyBoard(data);
      // No onChanged aquí: el padre hace load() completo y montaba un bucle.
      setSaveState("saved");
      return true;
    } catch (err) {
      setSaveState("error");
      setError(err?.response?.data?.error || "No se pudo guardar el layout.");
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- applyBoard es local estable en práctica
  }, [moodboardId, exporting]);

  const persistMeta = useCallback(async () => {
    if (!moodboardId || exporting || skipAutosaveRef.current) return false;
    const m = metaRef.current;
    setSaveState("saving");
    try {
      const data = await updateSkillAgentMoodboard(moodboardId, {
        Titulo: m.titulo,
        CategoryLabel: m.categoryLabel,
        StudioLine: m.studioLine,
      });
      applyBoard(data);
      setSaveState("saved");
      return true;
    } catch (err) {
      setSaveState("error");
      setError(err?.response?.data?.error || "No se pudo guardar los datos.");
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moodboardId, exporting]);

  const flushAutosave = useCallback(async () => {
    if (pagesTimerRef.current) {
      clearTimeout(pagesTimerRef.current);
      pagesTimerRef.current = null;
    }
    if (metaTimerRef.current) {
      clearTimeout(metaTimerRef.current);
      metaTimerRef.current = null;
    }
    const okPages = await persistPages();
    const okMeta = await persistMeta();
    return okPages && okMeta;
  }, [persistPages, persistMeta]);

  // Autosave layout (debounce)
  useEffect(() => {
    if (loading || skipAutosaveRef.current || exporting) return undefined;
    setSaveState((s) => (s === "saved" ? "unsaved" : s));
    if (pagesTimerRef.current) clearTimeout(pagesTimerRef.current);
    pagesTimerRef.current = setTimeout(() => {
      persistPages();
    }, 750);
    return () => {
      if (pagesTimerRef.current) clearTimeout(pagesTimerRef.current);
    };
  }, [pages, loading, exporting, persistPages]);

  // Autosave título / barras PDF
  useEffect(() => {
    if (loading || skipAutosaveRef.current || exporting) return undefined;
    setSaveState((s) => (s === "saved" ? "unsaved" : s));
    if (metaTimerRef.current) clearTimeout(metaTimerRef.current);
    metaTimerRef.current = setTimeout(() => {
      persistMeta();
    }, 900);
    return () => {
      if (metaTimerRef.current) clearTimeout(metaTimerRef.current);
    };
  }, [titulo, categoryLabel, studioLine, loading, exporting, persistMeta]);

  // Aviso si cierran la pestaña con cambios pendientes
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (saveState === "unsaved" || saveState === "saving") {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [saveState]);

  const handleSaveAll = async () => {
    setBusy(true);
    setError("");
    try {
      const ok = await flushAutosave();
      if (ok) {
        onChangedRef.current?.();
        setOkMsg("Guardado.");
      }
    } finally {
      setBusy(false);
    }
  };

  const assignAssetToSlot = (slotId, assetId) => {
    updateCurrentPageSlots((slots) =>
      slots.map((s) =>
        s.id === slotId ? { ...s, assetId: Number(assetId) } : s,
      ),
    );
  };

  const placeAssetInFirstEmpty = (assetId) => {
    setPages((prev) => {
      const next = prev.map((p) => normalizeFreePage(p));
      for (let i = 0; i < next.length; i += 1) {
        const empty = next[i].slots.find((s) => s.assetId == null);
        if (empty) {
          empty.assetId = Number(assetId);
          setPageIdx(i);
          setActiveSlotId(empty.id);
          return next;
        }
      }
      const page = emptyFreePage();
      page.slots[0].assetId = Number(assetId);
      setPageIdx(next.length);
      setActiveSlotId(page.slots[0].id);
      return [...next, page];
    });
  };

  const handleAddSlot = () => {
    const maxZ = Math.max(
      0,
      ...(currentPage.slots || []).map((s) => Number(s.z) || 0),
    );
    const slot = clampSlot({
      id: newSlotId(),
      x: 0.35,
      y: 0.35,
      w: 0.28,
      h: 0.3,
      shape: "rect",
      z: maxZ + 1,
      assetId: null,
    });
    updateCurrentPageSlots((slots) => [...slots, slot]);
    setActiveSlotId(slot.id);
  };

  const handleAddCircleSlot = () => {
    const maxZ = Math.max(
      0,
      ...(currentPage.slots || []).map((s) => Number(s.z) || 0),
    );
    // Landscape page ~297/210 → keep visual circle-ish defaults.
    const slot = clampSlot({
      id: newSlotId("c"),
      x: 0.38,
      y: 0.32,
      w: 0.2,
      h: 0.28,
      shape: "circle",
      z: maxZ + 1,
      assetId: null,
    });
    updateCurrentPageSlots((slots) => [...slots, slot]);
    setActiveSlotId(slot.id);
  };

  const handleToggleSlotShape = () => {
    if (!activeSlotId) return;
    updateCurrentPageSlots((slots) =>
      slots.map((s) => {
        if (s.id !== activeSlotId) return s;
        const nextShape = s.shape === "circle" ? "rect" : "circle";
        return clampSlot({ ...s, shape: nextShape });
      }),
    );
  };

  const handleBringToFront = () => {
    if (!activeSlotId) return;
    updateCurrentPageSlots((slots) => {
      const maxZ = Math.max(0, ...slots.map((s) => Number(s.z) || 0));
      return slots.map((s) =>
        s.id === activeSlotId ? clampSlot({ ...s, z: maxZ + 1 }) : s,
      );
    });
  };

  const handleSendToBack = () => {
    if (!activeSlotId) return;
    updateCurrentPageSlots((slots) => {
      const minZ = Math.min(...slots.map((s) => Number(s.z) || 0));
      return slots.map((s) =>
        s.id === activeSlotId
          ? clampSlot({ ...s, z: Math.max(0, minZ - 1) })
          : s,
      );
    });
  };

  const handleRemoveBackground = async () => {
    const assetId = activeSlot?.assetId;
    if (!moodboardId || !assetId || busy) return;
    setBusy(true);
    setError("");
    setOkMsg("");
    try {
      const data = await removeBgSkillAgentMoodboardAsset(moodboardId, assetId);
      if (Array.isArray(data?.assets)) setAssets(data.assets);
      if (Array.isArray(data?.PagesJson)) {
        setPages(normalizePages(data.PagesJson));
      } else if (data?.asset?.id) {
        const nextId = Number(data.asset.id);
        const fromId = Number(assetId);
        setPages((prev) =>
          prev.map((p) => ({
            ...normalizeFreePage(p),
            slots: normalizeFreePage(p).slots.map((s) =>
              Number(s.assetId) === fromId
                ? clampSlot({ ...s, assetId: nextId })
                : s,
            ),
          })),
        );
      }
      if (data?.Titulo != null || data?.id) {
        setBoard((p) => ({ ...p, ...data, assets: undefined }));
      }
      setOkMsg("Fondo quitado · PNG transparente en la casilla");
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err.message ||
          "No se pudo quitar el fondo",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveSlot = (slotId) => {
    const id = slotId || activeSlotId;
    if (!id) return;
    updateCurrentPageSlots((slots) => {
      if (slots.length <= 1) return slots;
      return slots.filter((s) => s.id !== id);
    });
    setActiveSlotId(null);
    setSlotRemoveId(null);
  };

  const handleAviatorPreset = () => {
    setPages((prev) =>
      prev.map((p, i) =>
        i === pageIdx ? applyAviatorPresetToPage(p) : normalizeFreePage(p),
      ),
    );
    setActiveSlotId(null);
  };

  const placeAddedAssets = (addedList, basePages, startPageIdx) => {
    const added = Array.isArray(addedList) ? addedList : [];
    if (!added.length) return basePages;
    let pagesNext = (basePages || []).map((p) => normalizeFreePage(p));
    let pi = startPageIdx;
    const already = new Set(
      pagesNext.flatMap((p) =>
        p.slots.map((s) => s.assetId).filter((id) => id != null),
      ),
    );
    for (const asset of added) {
      const id = Number(asset.id);
      if (already.has(id)) continue;
      let placed = false;
      const active = pagesNext[pi]?.slots?.find(
        (s) => s.id === activeSlotId && s.assetId == null,
      );
      if (active) {
        active.assetId = id;
        already.add(id);
        placed = true;
      }
      if (!placed) {
        for (let i = 0; i < pagesNext.length; i += 1) {
          const empty = pagesNext[i].slots.find((s) => s.assetId == null);
          if (empty) {
            empty.assetId = id;
            already.add(id);
            pi = i;
            placed = true;
            break;
          }
        }
      }
      if (!placed) {
        const page = emptyFreePage();
        page.slots[0].assetId = id;
        already.add(id);
        pagesNext = [...pagesNext, page];
        pi = pagesNext.length - 1;
      }
    }
    setPageIdx(pi);
    return pagesNext;
  };

  const handleAddFromPool = async (idsOverride) => {
    const rawIds = Array.isArray(idsOverride) ? idsOverride : [...poolPick];
    const ids = rawIds
      .map((id) => Number(id))
      .filter((id) => id && !usedSearchItemIds.has(id));
    if (!ids.length) return;
    setBusy(true);
    setError("");
    try {
      const data = await addSkillAgentMoodboardAssets(moodboardId, {
        searchItemIds: ids,
      });
      const added = Array.isArray(data?.added) ? data.added : [];
      const serverPages = normalizePages(data?.PagesJson);
      const placedOnServer = added.every((a) =>
        serverPages.some((p) =>
          p.slots.some((s) => Number(s.assetId) === Number(a.id)),
        ),
      );
      if (Array.isArray(data?.assets)) setAssets(data.assets);
      setBoard(data);
      if (placedOnServer) {
        skipAutosaveRef.current = true;
        setPages(serverPages);
        setTimeout(() => {
          skipAutosaveRef.current = false;
        }, 80);
      } else {
        // Colocación local → el autosave persiste el layout
        setPages(placeAddedAssets(added, serverPages, pageIdx));
      }
      setPoolPick(new Set());
      onChanged?.();
      setOkMsg(`${added.length || ids.length} foto(s) añadidas del SEARCH.`);
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudieron añadir.");
    } finally {
      setBusy(false);
    }
  };

  const handleAddOneFromPool = async (item) => {
    if (!item?.id || busy) return;
    await handleAddFromPool([item.id]);
  };

  const availablePoolItems = useMemo(
    () =>
      (pool.items || []).filter((it) => !usedSearchItemIds.has(Number(it.id))),
    [pool.items, usedSearchItemIds],
  );

  const handleFillPageFromPool = async () => {
    const emptyCount = currentPage.slots.filter(
      (s) => s.assetId == null,
    ).length;
    const take = availablePoolItems
      .slice(0, Math.max(emptyCount, 7))
      .map((it) => Number(it.id));
    if (!take.length) return;
    await handleAddFromPool(take);
  };

  const handleUpload = async (fileList) => {
    const files = [...(fileList || [])];
    if (!files.length) return;
    setBusy(true);
    setError("");
    try {
      const data = await addSkillAgentMoodboardAssets(moodboardId, { files });
      const added = Array.isArray(data?.added) ? data.added : [];
      const serverPages = normalizePages(data?.PagesJson);
      const placedOnServer = added.every((a) =>
        serverPages.some((p) =>
          p.slots.some((s) => Number(s.assetId) === Number(a.id)),
        ),
      );
      if (Array.isArray(data?.assets)) setAssets(data.assets);
      setBoard(data);
      if (placedOnServer) {
        skipAutosaveRef.current = true;
        setPages(serverPages);
        setTimeout(() => {
          skipAutosaveRef.current = false;
        }, 80);
      } else {
        setPages(placeAddedAssets(added, serverPages, pageIdx));
      }
      onChanged?.();
      setOkMsg(`${files.length} subida(s).`);
    } catch (err) {
      setError(err?.response?.data?.error || "Error al subir.");
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveAsset = async (assetId) => {
    setBusy(true);
    try {
      const data = await deleteSkillAgentMoodboardAsset(moodboardId, assetId);
      applyBoard(data);
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo quitar.");
    } finally {
      setBusy(false);
    }
  };

  const handleAutoPages = async () => {
    setBusy(true);
    try {
      const data = await autoSkillAgentMoodboardPages(moodboardId);
      applyBoard(data);
      setPageIdx(0);
      setActiveSlotId(null);
      onChanged?.();
      setOkMsg("Páginas repartidas (Aviator 7 / página).");
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo repartir.");
    } finally {
      setBusy(false);
    }
  };

  const handleExport = async () => {
    if (exporting || busy) return;
    setExporting(true);
    setBusy(true);
    setError("");
    setOkMsg("");
    try {
      await flushAutosave();
      const data = await exportSkillAgentMoodboardPdf(moodboardId, {
        customerFolderName: board?.CustomerName || undefined,
      });
      applyBoard(data.moodboard || data.search || data);

      if (data.pdfBase64) {
        try {
          const bin = atob(data.pdfBase64);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
          const blob = new Blob([bytes], { type: "application/pdf" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = data.pdfFileName || "moodboard.pdf";
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 2000);
        } catch {
          /* download optional */
        }
      }

      onChanged?.();
      if (data.drivePdfFileUrl) {
        setOkMsg("PDF exportado y subido a Drive (también descargado).");
      } else if (data.driveNote) {
        setOkMsg(`PDF descargado. Drive: ${data.driveNote}`);
      } else {
        setOkMsg("PDF generado y descargado.");
      }
    } catch (err) {
      setError(err?.response?.data?.error || "Error al exportar PDF.");
    } finally {
      setExporting(false);
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await deleteSkillAgentMoodboard(moodboardId);
      setDeleteOpen(false);
      onDeleted?.(moodboardId);
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo eliminar.");
      setBusy(false);
    }
  };

  const catParts = splitCategoryLabel(categoryLabel);
  const studioParts = splitStudioLine(studioLine);

  if (loading && !board) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!board) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Moodboard no encontrado.
      </Alert>
    );
  }

  return (
    <Stack spacing={1.5} sx={{ p: { xs: 1.5, md: 2 } }}>
      <Backdrop
        open={exporting}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          bgcolor: alpha("#0f172a", 0.55),
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <CircularProgress size={48} sx={{ color: "#fff" }} />
        <Typography fontWeight={800} sx={{ color: "#fff", fontSize: 18 }}>
          Exportando PDF…
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
          Guardando páginas y generando el archivo
        </Typography>
      </Backdrop>

      {/* Cabecera: título + 1 CTA */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1.5}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6" fontWeight={800} noWrap>
              {titulo || board.Titulo}
            </Typography>
            <Chip
              size="small"
              label={moodboardStatusLabel(board.Status)}
              color={board.Status === "ready" ? "success" : "default"}
              sx={{ height: 22, fontWeight: 700, flexShrink: 0 }}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" noWrap>
            {[board.CustomerName, board.Temporada].filter(Boolean).join(" · ")}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
          <Button
            size="small"
            variant="contained"
            startIcon={
              exporting ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <PictureAsPdfOutlinedIcon />
              )
            }
            disabled={busy || exporting || !assets.length}
            onClick={handleExport}
            sx={pagePrimaryButtonSx}
          >
            {exporting ? "Exportando…" : "Exportar PDF"}
          </Button>
          {board.DrivePdfFileUrl ? (
            <Tooltip title="Abrir PDF en Drive">
              <IconButton
                size="small"
                href={board.DrivePdfFileUrl}
                target="_blank"
                rel="noopener noreferrer"
                component="a"
              >
                <OpenInNewOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          <Tooltip title="Más opciones">
            <IconButton
              size="small"
              onClick={(e) => setLayoutMenuEl(e.currentTarget)}
              aria-label="Más opciones"
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={layoutMenuEl}
            open={Boolean(layoutMenuEl)}
            onClose={() => setLayoutMenuEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem
              disabled={busy}
              onClick={() => {
                setLayoutMenuEl(null);
                setMetaOpen(true);
              }}
            >
              Editar título / barras PDF
            </MenuItem>
            <Divider />
            <MenuItem
              disabled={busy}
              onClick={() => {
                setLayoutMenuEl(null);
                setDeleteOpen(true);
              }}
              sx={{ color: "error.main" }}
            >
              Eliminar moodboard
            </MenuItem>
          </Menu>
        </Stack>
      </Stack>

      {error ? (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}
      {okMsg ? (
        <Alert severity="success" onClose={() => setOkMsg("")}>
          {okMsg}
        </Alert>
      ) : null}

      {/* Meta colapsable — fuera del camino visual */}
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Button
          fullWidth
          onClick={() => setMetaOpen((v) => !v)}
          endIcon={metaOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{
            justifyContent: "space-between",
            textTransform: "none",
            fontWeight: 700,
            color: "text.secondary",
            px: 1.5,
            py: 0.75,
            borderRadius: 0,
          }}
        >
          Título y barras del PDF
        </Button>
        <Collapse in={metaOpen}>
          <Stack spacing={1.25} sx={{ px: 1.5, pb: 1.5, pt: 0.5 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
              <TextField
                size="small"
                label="Título"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                fullWidth
                sx={pageFieldSx}
              />
              <TextField
                size="small"
                label="Barra superior (cliente / temporada)"
                placeholder="Zara Global Urban · SS27"
                value={categoryLabel}
                onChange={(e) => setCategoryLabel(e.target.value)}
                fullWidth
                sx={pageFieldSx}
              />
            </Stack>
            <TextField
              size="small"
              label="Barra inferior (studio / contacto)"
              value={studioLine}
              onChange={(e) => setStudioLine(e.target.value)}
              fullWidth
              sx={pageFieldSx}
            />
          </Stack>
        </Collapse>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 340px" },
          gap: 2,
          alignItems: "start",
        }}
      >
        {/* Canvas + toolbar compacta */}
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
              mb: 1,
              gap: 0.5,
              flexWrap: "wrap",
            }}
          >
            <Tabs
              value={pageIdx}
              onChange={(_, v) => {
                setPageIdx(v);
                setActiveSlotId(null);
              }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 36,
                flex: 1,
                minWidth: 0,
                "& .MuiTab-root": {
                  minHeight: 36,
                  py: 0,
                  px: 1.25,
                  fontWeight: 700,
                  textTransform: "none",
                },
              }}
            >
              {pages.map((_, i) => (
                <Tab key={i} label={`Pág. ${i + 1}`} />
              ))}
            </Tabs>

            <Tooltip title="Añadir página">
              <IconButton
                size="small"
                onClick={() => {
                  setPages((p) => [
                    ...p.map(normalizeFreePage),
                    emptyFreePage(),
                  ]);
                  setPageIdx(pages.length);
                  setActiveSlotId(null);
                }}
              >
                <AddOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Añadir casilla cuadrada">
              <IconButton size="small" onClick={handleAddSlot}>
                <CropFreeOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Añadir casilla circular">
              <IconButton size="small" onClick={handleAddCircleSlot}>
                <CircleOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip
              title={
                activeSlot?.shape === "circle"
                  ? "Pasar a cuadrada"
                  : "Pasar a circular"
              }
            >
              <span>
                <IconButton
                  size="small"
                  disabled={!activeSlotId}
                  onClick={handleToggleSlotShape}
                >
                  {activeSlot?.shape === "circle" ? (
                    <CropSquareOutlinedIcon fontSize="small" />
                  ) : (
                    <CircleOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Traer al frente">
              <span>
                <IconButton
                  size="small"
                  disabled={!activeSlotId}
                  onClick={handleBringToFront}
                >
                  <FlipToFrontOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Enviar atrás">
              <span>
                <IconButton
                  size="small"
                  disabled={!activeSlotId}
                  onClick={handleSendToBack}
                >
                  <FlipToBackOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Quitar fondo (PNG transparente)">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={busy || !activeSlot?.assetId}
                  onClick={handleRemoveBackground}
                >
                  <AutoFixHighOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Plantilla Aviator (layout 1+6)">
              <IconButton size="small" onClick={handleAviatorPreset}>
                <ViewQuiltOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Reparte TODAS las fotos del moodboard en páginas Aviator (7 por página). Sustituye el layout actual.">
              <span>
                <IconButton
                  size="small"
                  disabled={busy || !assets.length}
                  onClick={handleAutoPages}
                >
                  <GridViewOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

            <Tooltip
              title={
                saveState === "error"
                  ? "Error al guardar — clic para reintentar"
                  : saveState === "unsaved"
                    ? "Hay cambios pendientes (se guardan solos)"
                    : saveState === "saving"
                      ? "Guardando…"
                      : "Todo guardado"
              }
            >
              <Button
                size="small"
                variant="text"
                disabled={busy || saveState === "saving"}
                onClick={handleSaveAll}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  flexShrink: 0,
                  minWidth: 0,
                  px: 1,
                  color:
                    saveState === "error"
                      ? "error.main"
                      : saveState === "unsaved"
                        ? "warning.dark"
                        : "text.secondary",
                }}
              >
                {saveState === "saving"
                  ? "Guardando…"
                  : saveState === "unsaved"
                    ? "Sin guardar…"
                    : saveState === "error"
                      ? "Reintentar"
                      : "Guardado"}
              </Button>
            </Tooltip>
          </Stack>

          <Box
            sx={{
              position: "relative",
              bgcolor: "#fff",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: "10px",
              width: "100%",
              aspectRatio: "297 / 210",
              boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
            }}
          >
            <Box
              ref={canvasRef}
              onClick={() => setActiveSlotId(null)}
              sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                overflow: "hidden",
                bgcolor: "#f3f3f3",
              }}
            >
              {layeredSlots.map((slot) => (
                <FreeformSlotBox
                  key={slot.id}
                  slot={slot}
                  selected={activeSlotId === slot.id}
                  canvasRef={canvasRef}
                  canRemove={currentPage.slots.length > 1}
                  onSelect={setActiveSlotId}
                  onRequestRemove={(id) => setSlotRemoveId(id)}
                  onChange={(next) => {
                    updateCurrentPageSlots((slots) =>
                      slots.map((s) => (s.id === next.id ? next : s)),
                    );
                  }}
                />
              ))}

              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  bgcolor: "#383838",
                  color: "#fff",
                  px: 1.75,
                  py: 0.75,
                  zIndex: 8,
                  pointerEvents: "none",
                  maxWidth: "38%",
                  display: "flex",
                  alignItems: "baseline",
                  gap: 0.5,
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 800,
                    fontSize: "0.85rem",
                    lineHeight: 1.2,
                    color: "#fff",
                  }}
                >
                  {catParts.main || "Moodboard"}
                </Typography>
                {catParts.sub ? (
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 400,
                      fontSize: "0.8rem",
                      lineHeight: 1.2,
                      color: "rgba(255,255,255,0.72)",
                    }}
                  >
                    - {catParts.sub}
                  </Typography>
                ) : null}
              </Box>

              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  bgcolor: "#383838",
                  color: "#fff",
                  px: 1.5,
                  py: 0.85,
                  zIndex: 8,
                  pointerEvents: "none",
                  maxWidth: "42%",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: "0.72rem",
                    lineHeight: 1.2,
                    color: "#8cd4e0",
                  }}
                >
                  {studioParts.studioName}
                </Typography>
                {studioParts.contactLine ? (
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: "0.58rem",
                      lineHeight: 1.35,
                      color: "#fff",
                      mt: 0.25,
                    }}
                  >
                    {studioParts.contactLine}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.75 }}
          >
            {activeSlotId
              ? "Arrastra = mover foto · −/+ = zoom · Shift = mover casilla · ○/□ forma · capas · varita = quitar fondo."
              : "Clic en una casilla. Añade cuadrada u ○ circular; varita = quitar fondo (PNG transparente para apilar)."}
          </Typography>
        </Box>

        {/* Rail fotos: Pool primero */}
        <Stack
          spacing={1}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            p: 1.25,
            bgcolor: alpha("#0f172a", 0.015),
            minHeight: { lg: 420 },
          }}
        >
          <Tabs
            value={photosTab}
            onChange={(_, v) => setPhotosTab(v)}
            variant="fullWidth"
            sx={{
              minHeight: 36,
              "& .MuiTab-root": {
                minHeight: 36,
                py: 0,
                textTransform: "none",
                fontWeight: 800,
                fontSize: "0.8rem",
              },
            }}
          >
            <Tab label={`Pool (${availablePoolItems.length})`} />
            <Tab label={`Moodboard (${assets.length})`} />
          </Tabs>

          {photosTab === 0 ? (
            <Stack spacing={1} sx={{ flex: 1, minHeight: 0 }}>
              <Typography variant="caption" color="text.secondary">
                Fotos de todos los SEARCH de este cliente
                {board?.Temporada ? ` · ${board.Temporada}` : ""}. Misma imagen
                en Drive no se vuelve a bajar; aquí puedes mezclar temas.
              </Typography>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Button
                  size="small"
                  variant="contained"
                  disabled={busy || availablePoolItems.length === 0}
                  onClick={handleFillPageFromPool}
                  sx={{
                    ...pagePrimaryButtonSx,
                    textTransform: "none",
                    fontWeight: 800,
                    flex: 1,
                  }}
                >
                  Llenar huecos
                </Button>
                <Tooltip title="Subir desde el PC (solo este moodboard)">
                  <Button
                    size="small"
                    component="label"
                    variant="outlined"
                    disabled={busy}
                    sx={{ minWidth: 40, px: 1 }}
                  >
                    <CloudUploadOutlinedIcon fontSize="small" />
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      multiple
                      onChange={(e) => {
                        handleUpload(e.target.files);
                        e.target.value = "";
                      }}
                    />
                  </Button>
                </Tooltip>
              </Stack>
              {poolPick.size > 0 ? (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={busy}
                  onClick={() => handleAddFromPool()}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  Añadir marcadas ({poolPick.size})
                </Button>
              ) : null}
              {poolError ? (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  {poolError}
                </Alert>
              ) : null}
              <Typography variant="caption" color="text.secondary">
                Clic = añadir. Marca varias y usa «Añadir marcadas».
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 0.75,
                  maxHeight: { xs: 360, lg: 520 },
                  overflow: "auto",
                  alignContent: "start",
                  pr: 0.25,
                }}
              >
                {poolLoading ? (
                  <Box
                    sx={{
                      gridColumn: "1 / -1",
                      display: "grid",
                      placeItems: "center",
                      py: 4,
                    }}
                  >
                    <CircularProgress size={22} />
                  </Box>
                ) : (
                  availablePoolItems.slice(0, poolShowCount).map((it) => (
                    <PoolThumb
                      key={it.id}
                      item={it}
                      height={132}
                      checked={poolPick.has(Number(it.id))}
                      onAdd={handleAddOneFromPool}
                      onToggle={(on) => {
                        setPoolPick((prev) => {
                          const next = new Set(prev);
                          if (on) next.add(Number(it.id));
                          else next.delete(Number(it.id));
                          return next;
                        });
                      }}
                    />
                  ))
                )}
              </Box>
              {!poolLoading && availablePoolItems.length > poolShowCount ? (
                <Button
                  size="small"
                  sx={{ textTransform: "none" }}
                  onClick={() => setPoolShowCount((n) => n + 24)}
                >
                  Ver más ({availablePoolItems.length - poolShowCount})
                </Button>
              ) : null}
              {!poolLoading && !availablePoolItems.length ? (
                <Typography variant="caption" color="text.secondary">
                  Pool vacío. Lanza SEARCH de este cliente/temporada o sube
                  archivos.
                </Typography>
              ) : null}
            </Stack>
          ) : (
            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary">
                Clic en una miniatura para colocarla en un hueco (o en la
                casilla seleccionada).
              </Typography>
              <Button
                size="small"
                component="label"
                variant="outlined"
                disabled={busy}
                startIcon={<CloudUploadOutlinedIcon />}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Subir desde el PC
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    handleUpload(e.target.files);
                    e.target.value = "";
                  }}
                />
              </Button>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 0.75,
                  maxHeight: { xs: 360, lg: 520 },
                  overflow: "auto",
                }}
              >
                {assets.map((a) => (
                  <Box key={a.id} sx={{ position: "relative", height: 72 }}>
                    <AssetThumb
                      assetId={a.id}
                      selected={false}
                      onClick={() => {
                        if (activeSlotId) assignAssetToSlot(activeSlotId, a.id);
                        else placeAssetInFirstEmpty(a.id);
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveAsset(a.id)}
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        bgcolor: alpha("#fff", 0.85),
                        p: 0.25,
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
              {!assets.length ? (
                <Typography variant="caption" color="text.secondary">
                  Aún no hay fotos. Ve a Pool o súbelas.
                </Typography>
              ) : null}
            </Stack>
          )}
        </Stack>
      </Box>

      <Dialog
        open={Boolean(slotRemoveId)}
        onClose={() => setSlotRemoveId(null)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>Quitar casilla</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Se elimina esta casilla de la página. La foto sigue en el moodboard
            (pestaña Moodboard) si quieres volver a colocarla.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSlotRemoveId(null)}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => handleRemoveSlot(slotRemoveId)}
          >
            Quitar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteOpen}
        onClose={() => !busy && setDeleteOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>Eliminar moodboard</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Se borra del CRM y el PDF/carpeta de Drive van a la papelera.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={busy}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy}
            onClick={handleDelete}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
